import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { toRFC3339 } from './datetime'
import { listQuery } from './query'

const api = clientFor('/scr/api')

/** 需求状态机（spec §5.1）：new → planned → responded → closed */
export type DemandStatus = 'new' | 'planned' | 'responded' | 'closed'

/** 优先级：与后端 internal/demand/service.go 的 validateInput 同源两份 */
export type DemandPriority = 'low' | 'normal' | 'high'

/** 需求来源：手工登记 / 预测信号 / 订单触发 */
export type DemandSource = 'manual' | 'forecast' | 'order'

/** 需求级动作，与后端 POST /demands/:id/plan|respond|close 及编辑入口一一对应 */
export type DemandAction = 'edit' | 'plan' | 'respond' | 'close'

/** 一条需求。字段与 services/scr/internal/demand/model.go 的 Demand 一一对应。 */
export interface Demand {
  id: number
  tenant_id: number
  /** 需求单号，前缀 XQ */
  doc_no: string
  item_name: string
  qty: number
  region: string
  source: DemandSource
  /** 预计到货/需求时间，后端 tag 带 omitempty，没填时字段不出现 */
  forecast_at?: string | null
  priority: DemandPriority
  status: DemandStatus
  remark: string
  created_at: string
  updated_at: string
}

/** 列表行与详情都是 Demand 本身（DemandListRow / DemandDetail 只是内嵌） */
export type DemandRow = Demand
export type DemandDetail = Demand

/** 新建 / 编辑入参：单号与 status 由服务端处理，都不在入参里 */
export interface DemandInput {
  item_name: string
  qty: number
  region: string
  source: DemandSource
  priority: DemandPriority
  forecast_at?: string
  remark?: string
}

export interface DemandQuery extends PageQuery {
  keyword?: string
  status?: string
  priority?: string
  source?: string
}

/** 表单态：空文本字段用 ''，没选时间用 null */
export interface DemandForm {
  item_name: string
  qty: number
  region: string
  source: DemandSource
  priority: DemandPriority
  forecast_at: string | null
  remark: string
}

/** 优先级字典，顺序与后端 validateInput 的判断顺序一致 */
export const PRIORITIES: DemandPriority[] = ['low', 'normal', 'high']

/** 来源字典，顺序与后端 validateInput 的判断顺序一致 */
export const SOURCES: DemandSource[] = ['manual', 'forecast', 'order']

/** 与后端 services/scr/internal/demand/service.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<DemandStatus, string> = {
  new: '新建',
  planned: '已规划',
  responded: '已响应',
  closed: '已关闭',
}

/** 优先级没有后端文案字典（只在 validateInput 里逐个比对），文案归前端 */
export const PRIORITY_LABELS: Record<DemandPriority, string> = {
  low: '低',
  normal: '中',
  high: '高',
}

/** 来源没有后端文案字典，文案归前端 */
export const SOURCE_LABELS: Record<DemandSource, string> = {
  manual: '手工登记',
  forecast: '预测信号',
  order: '订单触发',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<DemandStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> =
  {
    new: 'danger',
    planned: 'warning',
    responded: 'primary',
    closed: 'success',
  }

/** 优先级 → 表格里的标签色 */
export const PRIORITY_TAG: Record<DemandPriority, 'info' | 'warning' | 'danger'> = {
  low: 'info',
  normal: 'warning',
  high: 'danger',
}

/**
 * 各状态允许的动作，抄后端 internal/demand/service.go 的状态机：
 * new 能规划、planned 能标记已响应、三个未关闭状态都能编辑与关闭，closed 全部只读
 * （Update 与 Close 都先挡 closed，Close 再挡「已关闭」）。
 */
const ACTIONS: Record<DemandStatus, DemandAction[]> = {
  new: ['edit', 'plan', 'close'],
  planned: ['edit', 'respond', 'close'],
  responded: ['close'],
  closed: [],
}

/** 状态能不能触发某个动作（前端只做展示，最终以后端 409 为准） */
export function canAct(status: DemandStatus, action: DemandAction): boolean {
  return ACTIONS[status].includes(action)
}

/** 表单 → 请求体：空备注不进 body；没选 forecast_at 时字段整个不上送 */
export function demandInput(form: DemandForm): DemandInput {
  const input: DemandInput = {
    item_name: form.item_name.trim(),
    qty: Number(form.qty) || 0,
    region: form.region.trim(),
    source: form.source,
    priority: form.priority,
  }
  const forecastAt = toRFC3339(form.forecast_at)
  if (forecastAt) input.forecast_at = forecastAt
  const remark = form.remark.trim()
  if (remark) input.remark = remark
  return input
}

export const listDemands = (q: DemandQuery): Promise<PageResult<DemandRow>> =>
  api.get<PageResult<DemandRow>>('/demands', listQuery({ ...q }))

export const getDemand = (id: number): Promise<DemandDetail> => api.get<DemandDetail>(`/demands/${id}`)

export const createDemand = (input: DemandInput): Promise<Demand> => api.post<Demand>('/demands', input)

export const updateDemand = (id: number, input: DemandInput): Promise<Demand> =>
  api.put<Demand>(`/demands/${id}`, input)

/** 规划：new → planned。后端不读 body，这里仍发空对象与其它动作保持一致 */
export const planDemand = (id: number): Promise<Demand> => api.post<Demand>(`/demands/${id}/plan`, {})

/** 标记已响应：planned → responded */
export const respondDemand = (id: number): Promise<Demand> =>
  api.post<Demand>(`/demands/${id}/respond`, {})

/** 关闭：任何未关闭状态 → closed */
export const closeDemand = (id: number): Promise<Demand> => api.post<Demand>(`/demands/${id}/close`, {})

/** 下拉参照用的全量需求：后端没有 size 上限，这里按 200 取，与其他子系统的参照口径一致 */
export const listDemandOptions = (): Promise<Demand[]> =>
  listDemands({ page: 1, size: 200 }).then((page) => page.list)
